'use client';

import { ArrowForward } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  InputLabel,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import Button from '@/components/atoms/Button';
import { ContactFormData, submitContactForm } from '@/services';

// Custom styled TextField to match Figma design
const CustomTextField = styled(TextField)(({ theme: _theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    '& fieldset': {
      border: 'none',
      borderBottom: '1px solid #CCCCCC',
      borderRadius: 0,
    },
    '&:hover fieldset': {
      borderBottom: '1px solid #CCCCCC',
    },
    '&.Mui-focused fieldset': {
      borderBottom: '2px solid #EB722E',
    },
    '& input': {
      padding: '8px 0',
      fontSize: '16px',
      fontFamily: 'SF Pro, sans-serif',
      color: '#4D4C4C',
    },
    '& textarea': {
      padding: '8px 0',
      fontSize: '16px',
      fontFamily: 'SF Pro, sans-serif',
      color: '#4D4C4C',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '18px',
    fontFamily: 'SF Pro, sans-serif',
    fontWeight: 510,
    color: '#1A1A1A',
    transform: 'translate(0, -24px) scale(1)',
    '&.Mui-focused': {
      color: '#1A1A1A',
    },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: '#CCCCCC',
    opacity: 1,
  },
}));

export default function ContactFormSection() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({
    type: null,
    message: '',
  });

  const handleChange =
    (field: keyof ContactFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
      // Clear any previous submit status when user starts typing
      if (submitStatus.type) {
        setSubmitStatus({ type: null, message: '' });
      }
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Basic validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      setSubmitStatus({
        type: 'error',
        message: 'Vui lòng điền đầy đủ thông tin.',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        type: 'error',
        message: 'Email không hợp lệ.',
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      await submitContactForm(formData);

      setSubmitStatus({
        type: 'success',
        message:
          'Cảm ơn bạn đã liên hệ! Chúng tôi sẽ phản hồi trong thời gian sớm nhất.',
      });

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      // Check if it's a duplicate email error
      const errorMessage = error instanceof Error ? error.message : '';
      if (
        errorMessage.includes('24 giờ qua') ||
        errorMessage.includes('24 hours')
      ) {
        setSubmitStatus({
          type: 'error',
          message:
            'Bạn đã gửi yêu cầu liên hệ trong 24 giờ qua. Vui lòng chờ trước khi gửi lại.',
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: 'Có lỗi xảy ra khi gửi thông tin. Vui lòng thử lại.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '510px',
      }}
    >
      {/* Title Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h1'
          component='h1'
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '40px',
            lineHeight: 1.45,
            color: '#001A44',
            mb: 2,
            textAlign: 'left',
          }}
        >
          LIÊN HỆ
        </Typography>

        {/* Orange line */}
        <Box
          sx={{
            width: '151px',
            height: '5px',
            backgroundColor: '#EB722E',
            mb: 4,
          }}
        />
      </Box>

      {/* Contact Form */}
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* Name and Email Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3,
          }}
        >
          <Box sx={{ mb: 3 }}>
            <InputLabel
              htmlFor='contact-form-name'
              sx={{
                fontSize: '18px',
                fontFamily: 'SF Pro, sans-serif',
                fontWeight: 510,
                color: '#1A1A1A',
                mb: 1,
              }}
            >
              Họ và Tên
            </InputLabel>
            <CustomTextField
              fullWidth
              variant='outlined'
              value={formData.name}
              onChange={handleChange('name')}
              required
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <InputLabel
              htmlFor='contact-form-email'
              sx={{
                fontSize: '18px',
                fontFamily: 'SF Pro, sans-serif',
                fontWeight: 510,
                color: '#1A1A1A',
                mb: 1,
              }}
            >
              Email
            </InputLabel>
            <CustomTextField
              fullWidth
              variant='outlined'
              type='email'
              value={formData.email}
              onChange={handleChange('email')}
              required
            />
          </Box>
        </Box>

        {/* Subject Field */}
        <Box sx={{ mb: 3 }}>
          <InputLabel
            htmlFor='contact-form-subject'
            sx={{
              fontSize: '18px',
              fontFamily: 'SF Pro, sans-serif',
              fontWeight: 510,
              color: '#1A1A1A',
              mb: 1,
            }}
          >
            Tiêu đề
          </InputLabel>
          <CustomTextField
            fullWidth
            variant='outlined'
            value={formData.subject}
            onChange={handleChange('subject')}
            required
          />
        </Box>

        {/* Message Field */}
        <Box sx={{ mb: 3 }}>
          <InputLabel
            htmlFor='contact-form-message'
            sx={{
              fontSize: '18px',
              fontFamily: 'SF Pro, sans-serif',
              fontWeight: 510,
              color: '#1A1A1A',
              mb: 1,
            }}
          >
            Nội dung
          </InputLabel>
          <CustomTextField
            fullWidth
            variant='outlined'
            rows={4}
            value={formData.message}
            onChange={handleChange('message')}
            required
          />
        </Box>

        {/* Status Messages */}
        {submitStatus.type && (
          <Alert severity={submitStatus.type} sx={{ mb: 3 }}>
            {submitStatus.message}
          </Alert>
        )}

        {/* Submit Button */}
        <Box>
          <Button
            type='submit'
            variant='primary'
            size='large'
            icon={
              isSubmitting ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <ArrowForward />
              )
            }
            iconPosition='end'
            disabled={isSubmitting}
            sx={{
              width: '263px',
              height: '56px',
              borderRadius: '5px',
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'uppercase',
              backgroundColor: isSubmitting ? '#cccccc' : '#EB722E',
              '&:hover': {
                backgroundColor: isSubmitting ? '#cccccc' : '#D65A1A',
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
                color: '#666666',
              },
            }}
          >
            {isSubmitting ? 'đang gửi...' : 'gửi thông tin'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
